/**
 * The lane-v2 SESSION view: one conversation on MSA's session-keyed routes.
 *
 * WHAT IT OWNS: the snapshot GETs' latest answers (R1 task list, R2 asks, R3 per-task snapshots, R5
 * turns), the stream cursor, the user's own not-yet-acknowledged messages, and the stream's health.
 * The rules live in `services/multi-agent/laneV2/reduce.ts` (pure, tested); this store is the glue:
 * it attaches (R1 → R2 + R5 + R3s → stream from R1.last_seq), turns frames into debounced refetches,
 * and runs the user's actions.
 *
 * WHAT IT DOES NOT OWN: which lane a conversation is on (`laneV2/lane.ts`), or which conversation is
 * active (`weaveSessionStore` / `sessionIdentity.ts`). The route adopts a pinned v2 session into the
 * weave store so there is still one active-conversation owner.
 *
 * TENANT ISOLATION: registered as org-scoped (reset + stream abort on a switch), and the one persisted
 * field — the active v2 session id — lives in org-partitioned storage.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createOrgScopedJSONStorage, registerOrgScopedReset } from "@/lib/org-scope";
import * as api from "@/services/multi-agent/laneV2/api";
import { describeLaneV2Error, isLaneV2Error } from "@/services/multi-agent/laneV2/api";
import { abortableSleep, isAbortError, pollBranchSave, runBranchSave } from "@/services/multi-agent/laneV2/branchSaveRun";
import { deriveAfterCut, isLostAnswer, resumeFrom, type SaveBranchSettled } from "@/services/multi-agent/laneV2/branchSave";
import { answerRoute, turnTextForAnswer } from "@/services/multi-agent/laneV2/askRouting";
import { steerFailureCopy, canSendFollowUp, followUpFactsOf, isRunTerminal, settleFollowUps, unconfirmFollowUps } from "@/services/multi-agent/laneV2/followUps";
import { capabilitiesFromProbe, setCachedCapabilities } from "@/services/multi-agent/laneV2/lane";
import { resolveActiveOrgId, useAuthStoreV1 } from "@/stores/authStore_v1";
import {
  applyAsks,
  applyAttach,
  applySession,
  applySnapshot,
  applyTurns,
  bindLocalTurn,
  INITIAL_LANE_V2_STATE,
  connectorWriteOutcomeOf,
  reduceFrame,
  type LaneV2State,
  type RefetchPlan,
  type TrackedSave,
  reduceLiveFrame,
} from "@/services/multi-agent/laneV2/reduce";
import { laneV2IdeState } from "@/services/multi-agent/ideState";
import { createRefetchScheduler, type RefetchScheduler } from "@/services/multi-agent/laneV2/refetchScheduler";
import { openSessionStream, type SessionStreamDeps } from "@/services/multi-agent/laneV2/stream";
import type {
  AskView,
  AttachmentIn,
  IdeStateIn,
  ItemBody,
  LaneV2Session,
  SaveBranchIn,
  SessionFrameWire,
  TaskSnapshot,
  ValidateOut,
  RequestChangesOut,
  DiscardOut,
} from "@/services/multi-agent/laneV2/types";
import type { RespondBody } from "@/services/multi-agent/interactions";
import { respondVia } from "@/services/multi-agent/respondTarget";
import { useWeaveSessionStore } from "@/stores/weaveSessionStore";

export interface LaneV2CapabilityOpts {
  deployMode?: string | null;
  turnAnswersAsk?: boolean;
  offline?: boolean;
  turnAttachments?: boolean;
  turnIdeState?: boolean;
  branchSave?: boolean;
  liveFrames?: boolean;
}

/** 409 refusals of Request changes / Discard: the task is no longer where the card showed it. */
const REST_ACTION_REFUSALS: ReadonlySet<string> = new Set(["NOT_DISCARDABLE", "NOT_CHANGEABLE", "HUMAN_CHANGE_CAP_REACHED"]);

export interface LaneV2Actions {
  /** A brand-new conversation: a client-owned id nothing has been sent on (not pinned, not streamed). */
  startDraft: (sessionId: string, caps?: LaneV2CapabilityOpts) => void;
  /** Attach to an existing v2 session: R1 (or the one given) → R2 + R5 + R3s → stream. */
  attach: (sessionId: string, opts?: LaneV2CapabilityOpts & { session?: LaneV2Session | null }) => Promise<void>;
  /** Re-attach the current session (manual "Reconnect", tab back in focus after a disconnect). */
  reconnect: () => Promise<void>;
  /** Abort the stream and forget everything (org switch, leaving lane v2). */
  reset: () => void;
  /**
   * Submit a chat turn. Resolves once the request is IN FLIGHT; the turn's outcome arrives by stream
   * (via `state.localTurns`, keyed by the returned local id — `null` when nothing was actually sent:
   * an empty/no-session bail, or a draft that failed to pin). `answersAskId` continues a
   * `respond_then_turn`-routed ask deterministically (`askRouting.ts`). `attachments`/`ideState` are
   * only ever forwarded when the caller actually has them — see `submitTurn`'s own doc: a server
   * without `turn_attachments`/`turn_ide_state` refuses the field.
   */
  sendTurn: (
    text: string,
    opts?: {
      answersAskId?: string;
      attachments?: AttachmentIn[];
      ideState?: IdeStateIn | null;
      /**
       * Called once if the server ACCEPTED this send, never on a refusal or a failure: when the turn OPENS (`turn_opened`
       * binds the local turn — MSA validates attachments and refuses a busy turn before it opens one), or when a result
       * comes back ("done" or "detached"), whichever is first. On MSA the result comes only when the turn ENDS, so waiting
       * for it alone kept a sent image in the composer for the whole answer. It belongs to this send alone and does not
       * depend on the store's generation: a reset, a new conversation or a later send cannot drop it. The composer uses it
       * to remove what this send carried.
       */
      onAccepted?: () => void;
      /** This browser's picture of each image attachment, by name — kept on the echo only (`LocalAttachment.previewUrl`). */
      previews?: Readonly<Record<string, string>>;
    },
  ) => Promise<string | null>;
  /**
   * Send a message WHILE a turn is running: it goes to that turn's inbox (never a second turn, which the server
   * refuses), and shows in `state.followUps`. The turn's own result settles it: taken into the reply or the plan, or
   * sent by this store as the next turn. Resolves to the local id, or `null` when there is no running turn to send it
   * to (the caller sends a normal turn) or the limit of waiting messages is reached.
   */
  sendFollowUp: (text: string, opts?: { withoutFile?: boolean }) => Promise<string | null>;
  /** Drop one row of `followUps` the reader has dealt with (dismissed, or sent again). */
  dismissFollowUp: (localId: string) => void;
  cancelActiveTurn: () => Promise<void>;
  /**
   * Answer an ask through its owning route: `respond`, a `turn`, or (with the `turn_answers_ask`
   * capability) `respond` then a turn that continues it. Throws on refusal.
   */
  answerAsk: (ask: AskView, body: RespondBody) => Promise<void>;
  cancelTask: (taskId: string) => Promise<{ cancelled: boolean; reason: string | null }>;
  /**
   * `hold` / `resume` (the R3 `task_controls`): pause after the component in flight, or pick a held task up. Both refetch the
   * task (also after a refusal, so the buttons reflect where it stands). Throws `LaneV2Error` on a refusal
   * (`HOLD_BUDGET_STILL_EXHAUSTED`, `STALE_TRANSITION`, `LANE_V2_OFFLINE`); the typed `status` of a 200 is returned.
   */
  holdTask: (taskId: string) => Promise<{ status: string }>;
  resumeTask: (taskId: string) => Promise<{ status: string }>;
  revertTask: (taskId: string) => Promise<void>;
  /**
   * `POST .../tasks/{t}/save` (capability `branch_save`). Never touches the org or a run — writes to
   * the caller's own workspace branch. Throws `LaneV2Error` on refusal (`NO_PACKAGE`/`UNKNOWN_MEMBER`/
   * `NOTHING_INCLUDED`/`BRANCH_CONFLICT`/`BRANCH_SAVE_REJECTED`/`BRANCH_SAVE_REPLAYED`/
   * `BRANCH_SAVE_UNAVAILABLE`) — the caller reads those off the error with `branchConflictOf` /
   * `branchSaveRejectionOf` / `describeLaneV2Error`, same pattern as `sendTurn`/`answerAsk`.
   */
  saveBranch: (taskId: string, body: SaveBranchIn, opts?: SaveBranchRunOpts) => Promise<SaveBranchSettled>;
  /**
   * `POST .../tasks/{t}/validate` (contracts 0.2.30): re-run the check against the org. `idempotencyKey` is minted per
   * click by the caller (`newValidateKey`); the same key replays. Throws `LaneV2Error` (`VALIDATION_IN_PROGRESS` /
   * `NOT_VALIDATABLE` / `REVALIDATE_LIMIT_REACHED` / `DEPLOY_NOT_ENABLED`). Refetches the task, so the new check and the
   * task's `actions` land without waiting for the stream.
   */
  validateTask: (taskId: string, idempotencyKey: string) => Promise<ValidateOut>;
  /**
   * `POST .../tasks/{t}/request-changes` (RMV 14): send the resting changeset back with `text`. Throws `LaneV2Error`
   * (`FREE_TEXT_REQUIRED` / `NOT_CHANGEABLE` / `HUMAN_CHANGE_CAP_REACHED`). Refetches R1/R2/R3/R5 (a new plan round starts).
   */
  requestChanges: (taskId: string, text: string, idempotencyKey: string) => Promise<RequestChangesOut>;
  /** `POST .../tasks/{t}/discard` (RMV 14): the task ends declined. Throws `NOT_DISCARDABLE`. Refetches R1/R2/R3/R5. */
  discardTask: (taskId: string, idempotencyKey: string) => Promise<DiscardOut>;
  loadItemBody: (taskId: string, workItemId: string) => Promise<ItemBody>;
  /** Refetch now (bypasses the debounce). */
  refetch: (plan: RefetchPlan) => Promise<void>;
  /** Apply one frame (the stream calls this; exported for tests and the QA harness). */
  applyFrame: (frame: SessionFrameWire) => void;
}

export type LaneV2Store = LaneV2State & LaneV2Actions;

// ── injectable collaborators (tests swap these) ──────────────────────────────────────────────────

/** Per-call options of `saveBranch`: abort (unmount / session change) and the in-progress count of a background save. */
export interface SaveBranchRunOpts {
  signal?: AbortSignal;
  onProgress?: (chunksWritten: number) => void;
}

export interface LaneV2StoreDeps {
  api: Pick<
    typeof api,
    | "getSession"
    | "getAsks"
    | "getTask"
    | "getTurns"
    | "getItemBody"
    | "mintTicket"
    | "submitTurn"
    | "postTurnMessage"
    | "cancelTurn"
    | "cancelTask"
    | "holdTask"
    | "resumeTask"
    | "revertTask"
    | "saveBranch"
    | "getBranchSave"
    | "validateTask"
    | "requestChanges"
    | "discardTask"
    | "getCapabilities"
  >;
  respondVia: typeof respondVia;
  openStream: typeof openSessionStream;
  streamDeps?: SessionStreamDeps;
  schedulerDelayMs?: number;
  newLocalId: () => string;
  now: () => string;
}

const defaultDeps: LaneV2StoreDeps = {
  api,
  respondVia,
  openStream: openSessionStream,
  newLocalId: () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `local-${Date.now()}-${Math.random()}`),
  now: () => new Date().toISOString(),
};

let deps: LaneV2StoreDeps = defaultDeps;

/** Tests / QA harness only. */
export function setLaneV2StoreDeps(next: Partial<LaneV2StoreDeps> | null): void {
  deps = next ? { ...defaultDeps, ...next } : defaultDeps;
}

// ── the live resources (not state: they are handles) ─────────────────────────────────────────────

let stream: { abort: () => void } | null = null;
let scheduler: RefetchScheduler | null = null;
/** Bumped on every attach / reset: async results from an older generation are dropped. */
let generation = 0;
/**
 * Each in-flight send's `onAccepted`, by local id, so `turn_opened` (which binds the local turn) can fire it before the
 * POST comes back. Deliberately NOT cleared by `reset`: acceptance is a fact about that request, and the composer's own
 * reset epoch is what makes a late one touch nothing.
 */
const acceptOnOpen = new Map<string, () => void>();

/**
 * The single live offline re-probe loop, and which generation it is running for. At most one loop
 * is ever in flight: `markOffline` fires on every 503, and multiple calls for the SAME generation
 * (five 503s in quick succession, or the initial-R0-offline path racing a live 503) must share the
 * one loop already covering that generation rather than each starting their own — five 503s must
 * not mean five self-repeating `setTimeout` chains all polling R0 in parallel.
 */
let reprobeTimer: ReturnType<typeof setTimeout> | null = null;
let reprobeGen: number | null = null;

function stopReprobeLoop(): void {
  if (reprobeTimer !== null) clearTimeout(reprobeTimer);
  reprobeTimer = null;
  reprobeGen = null;
}

/** The branch saves this store is polling (task id → save id + its abort handle). Aborted with the generation. */
const activeSaves = new Map<string, { saveId: string; ctl: AbortController }>();

function abortActiveSaves(): void {
  for (const a of activeSaves.values()) a.ctl.abort();
  activeSaves.clear();
}

function stopLive(): void {
  abortActiveSaves();
  stream?.abort();
  stream = null;
  scheduler?.cancel();
  scheduler = null;
  stopReprobeLoop();
}

/** The ASK list the view reads: all (pending + the recent record), oldest first. */
const ASK_STATUS = "all" as const;

const PERSIST_NAME = "weave-lane-v2-session";

export const useLaneV2SessionStore = create<LaneV2Store>()(
  persist(
    (set, get) => {
      const current = (gen: number) => gen === generation;

      /**
       * Send the oldest queued message as the next turn, when the session is idle. One at a time: the next one goes
       * when that turn ends (`applyFrame` on `turn_ended`, and each turn's own result, call this again). `endedTurnId` is
       * a turn the caller KNOWS is over (its result just arrived) even if its `turn_ended` frame has not been seen yet.
       */
      function drainQueued(gen: number, endedTurnId?: string): void {
        if (!current(gen)) return;
        const st = get();
        if (!st.queuedTurns.length || st.offline) return;
        const busy = (!!st.activeTurnId && st.activeTurnId !== endedTurnId) || st.localTurns.some((t) => t.status === "sending");
        if (busy) return;
        const [next, ...rest] = st.queuedTurns;
        set({ queuedTurns: rest });
        void get().sendTurn(next);
      }

      /** An R3 snapshot lands: store it, then follow a background save it says is running (reload / second tab / chat seat). */
      function applySnap(gen: number, snap: TaskSnapshot): void {
        set((st) => applySnapshot(st, snap));
        resumeSaveFrom(gen, snap);
      }

      const setSave = (taskId: string, save: TrackedSave | null): void => {
        set((st) => {
          const saves = { ...st.saves };
          if (save) saves[taskId] = save;
          else delete saves[taskId];
          return { saves };
        });
      };

      const pollDeps = (sessionId: string, taskId: string, signal: AbortSignal) => ({
        poll: (id: string) => deps.api.getBranchSave(sessionId, taskId, id, { signal }),
        sleep: abortableSleep,
        signal,
      });

      /**
       * R3 `package.branch_save_running` is the only thing that says a save is running. `async` → poll that save id (unless
       * this store already is); `sync` → show it and wait for frames/refetches (no timer decides it is over); null → free.
       */
      function resumeSaveFrom(gen: number, snap: TaskSnapshot): void {
        const taskId = snap.task_id;
        const running = snap.package?.branch_save_running ?? null;
        const todo = resumeFrom(running);
        if (todo.action === "idle") {
          // The server says nothing runs: drop a stale running row (a failed row stays until the next save).
          const held = get().saves[taskId];
          if (held?.phase === "running" && !activeSaves.has(taskId)) setSave(taskId, null);
          // A failed entry is stale once R3 shows a newer history head than the one it was recorded against.
          if (held?.phase === "failed" && (snap.package?.branch_saves?.[0]?.at ?? null) !== held.headAt) setSave(taskId, null);
          return;
        }
        const sessionId = get().sessionId;
        if (!sessionId || !current(gen)) return;
        if (todo.action === "wait_sync") {
          if (!activeSaves.has(taskId)) setSave(taskId, { phase: "running", saveId: running?.save_id ?? null, mode: "sync", chunks: running?.chunks_written ?? 0 });
          return;
        }
        if (activeSaves.get(taskId)?.saveId === todo.saveId) return;
        const ctl = new AbortController();
        activeSaves.get(taskId)?.ctl.abort();
        activeSaves.set(taskId, { saveId: todo.saveId, ctl });
        setSave(taskId, { phase: "running", saveId: todo.saveId, mode: "async", chunks: running?.chunks_written ?? 0 });
        void pollBranchSave(
          {
            ...pollDeps(sessionId, taskId, ctl.signal),
            onProgress: (chunks) => {
              if (current(gen)) setSave(taskId, { phase: "running", saveId: todo.saveId, mode: "async", chunks });
            },
          },
          todo.saveId,
        ).then(
          () => { if (current(gen) && !ctl.signal.aborted) setSave(taskId, null); },
          async (err) => {
            if (!current(gen) || ctl.signal.aborted || isAbortError(err)) return;
            // MSA writes chunk rows before the job outcome: take the history head from a FRESH R3, not the stored one, so the
            // refetch after this failure does not see a "newer head" and wipe it at once.
            let headAt = get().snapshots[taskId]?.package?.branch_saves?.[0]?.at ?? null;
            try {
              const fresh = await deps.api.getTask(sessionId, taskId);
              if (!current(gen) || ctl.signal.aborted) return;
              set((st) => applySnapshot(st, fresh));
              headAt = fresh.package?.branch_saves?.[0]?.at ?? null;
            } catch (e) {
              console.warn("[lane-v2] task refetch before recording a save failure failed", e);
            }
            setSave(taskId, { phase: "failed", error: err, headAt });
          },
        ).finally(() => {
          if (activeSaves.get(taskId)?.ctl === ctl) activeSaves.delete(taskId);
          if (current(gen) && !ctl.signal.aborted) ensureScheduler(gen).schedule({ session: false, asks: false, turns: false, tasks: [taskId] });
        });
      }

      async function runRefetch(gen: number, plan: RefetchPlan): Promise<void> {
        const sid = get().sessionId;
        if (!sid || get().draft) return;
        const jobs: Promise<void>[] = [];
        if (plan.session) {
          jobs.push(
            deps.api.getSession(sid).then((s) => {
              if (current(gen)) set((st) => applySession(st, s));
              // A task the list now shows but we have no snapshot for is fetched too.
              const missing = (s.tasks ?? []).map((t) => t.task_id).filter((id) => !get().snapshots[id] && !plan.tasks.includes(id));
              return Promise.all(missing.map((id) => deps.api.getTask(sid, id).then((snap) => {
                if (current(gen)) applySnap(gen, snap);
              }))).then(() => undefined);
            }),
          );
        }
        if (plan.asks) {
          jobs.push(deps.api.getAsks(sid, ASK_STATUS).then((asks) => { if (current(gen)) set((st) => applyAsks(st, asks)); }));
        }
        if (plan.turns) {
          jobs.push(deps.api.getTurns(sid).then((turns) => { if (current(gen)) set((st) => applyTurns(st, turns)); }));
        }
        for (const taskId of plan.tasks) {
          jobs.push(deps.api.getTask(sid, taskId).then((snap) => { if (current(gen)) applySnap(gen, snap); }));
        }
        const results = await Promise.allSettled(jobs);
        const failed = results.find((r) => r.status === "rejected") as PromiseRejectedResult | undefined;
        if (failed) {
          console.warn("[lane-v2] snapshot refetch failed", failed.reason);
          if (current(gen)) set({ stale: true });
        } else if (current(gen) && get().stale) {
          set({ stale: false });
        }
      }

      function ensureScheduler(gen: number): RefetchScheduler {
        if (!scheduler) {
          scheduler = createRefetchScheduler((plan) => runRefetch(gen, plan), { delayMs: deps.schedulerDelayMs ?? 200 });
        }
        return scheduler;
      }

      function startStream(gen: number, sessionId: string): void {
        stream?.abort();
        stream = deps.openStream(
          sessionId,
          () => get().lastSeq,
          {
            onFrame: (frame) => {
              if (current(gen)) get().applyFrame(frame);
            },
            onLive: (frame) => {
              if (current(gen)) set((st) => reduceLiveFrame(st, frame));
            },
            onStatus: (status) => {
              if (!current(gen)) return;
              // The stream re-opens by itself (ticket lapse, stall watchdog) without a re-attach, and deltas
              // are not replayed: a draft kept across the gap would silently miss text. R5 holds the truth.
              set(status === "reconnecting" ? { streamStatus: status, liveDraft: null } : { streamStatus: status });
            },
            onFatal: (err) => {
              if (!current(gen)) return;
              set({
                error: isLaneV2Error(err) ? describeLaneV2Error(err) : "This conversation is no longer available.",
              });
            },
          },
          deps.streamDeps,
        );
      }

      /** Mint (pins the session to v2), start the stream. Only ever for a session resolved v2. */
      async function pinDraft(gen: number, sessionId: string): Promise<void> {
        await deps.api.mintTicket(sessionId);
        if (!current(gen)) return;
        set({ draft: false });
        startStream(gen, sessionId);
        // The pinned session becomes THE active conversation (one owner: `weaveSessionStore`), which
        // is also what restores it on reload.
        useWeaveSessionStore.getState().adopt(sessionId);
      }

      /** The re-probe cadence once no `Retry-After` is known (a raw `getCapabilities()` failure, or
       *  the initial-R0-said-offline case, which carries no HTTP response to read one off). */
      const OFFLINE_REPROBE_DEFAULT_MS = 60_000;

      /** The org this tab is currently scoped to — `authStore_v1.ts`'s shared `resolveActiveOrgId`,
       *  the same "actively selected, single-org fallback" rule `client.ts`, `useLaneV2Route.ts` and
       *  `useMergedSessions.ts` all use. */
      function currentOrgId(): string | null {
        return resolveActiveOrgId(useAuthStoreV1.getState());
      }

      /**
       * KEEPS GOING WHILE OFFLINE IS TRUE — a single one-shot probe left the banner stuck on forever
       * once its one answer was itself still offline (or unreachable): nothing else in this store ever
       * scheduled a second one unless the user happened to retry a real action and drew a fresh 503.
       * Every answer (online, still offline, or a thrown error) is written back to `lane.ts`'s
       * `capsCache` via `setCachedCapabilities` — without that, a LATER `startDraft`/`attach` (a new
       * draft, a tab reopened, `useLaneV2Route`'s own `resolveCapabilities`) would read the STALE
       * cached-per-org R0 answer from before the operator flipped the switch back and re-set `offline`
       * to `true` right after this loop had just cleared it.
       *
       * AT MOST ONE TIMER PER GENERATION: `reprobeGen` is set to `gen` for the whole time a loop owns
       * that generation — both while its `setTimeout` is pending AND while its probe request is in
       * flight — and only cleared right before this function re-claims it for the next tick, or by
       * `stopReprobeLoop` on reset/attach/an online answer. A second call for the SAME generation
       * (another 503 landing while this loop is already running) sees `reprobeGen === gen` and is a
       * no-op: the existing loop already covers it, so N 503s in a row still start only one loop.
       */
      function scheduleOfflineReprobe(gen: number, waitMs: number): void {
        if (reprobeGen === gen) return;
        reprobeGen = gen;
        reprobeTimer = setTimeout(() => {
          reprobeTimer = null;
          if (!current(gen)) { reprobeGen = null; return; }
          deps.api
            .getCapabilities()
            .then((raw) => {
              if (!current(gen)) { reprobeGen = null; return; }
              const caps = capabilitiesFromProbe({ ok: true, value: raw });
              setCachedCapabilities(currentOrgId(), caps);
              const stillOffline = caps.offline === true;
              set({ offline: stillOffline });
              reprobeGen = null;
              // Back online: the loop's job is done, nothing to reschedule.
              if (stillOffline) scheduleOfflineReprobe(gen, OFFLINE_REPROBE_DEFAULT_MS);
            })
            .catch(() => {
              // Still unreachable — keep trying at the default cadence (no `Retry-After` to read off a
              // thrown network error).
              reprobeGen = null;
              if (current(gen)) scheduleOfflineReprobe(gen, OFFLINE_REPROBE_DEFAULT_MS);
            });
        }, waitMs);
      }

      /**
       * A `503 LANE_V2_OFFLINE` on any call (never gated by the cached `resolveCapabilities` — R0 is
       * read live) flips the same `offline` state the attach-time R0 answer sets, and starts the
       * re-probe loop above at the server's own `Retry-After` (falling back to the default cadence)
       * so the banner clears itself the moment the operator flips the switch back, without the user
       * having to reload. Safe to call more than once for the same generation in quick succession
       * (several in-flight calls all drawing a 503 together) -- `scheduleOfflineReprobe` is the one
       * that de-dupes the loop itself; this just sets `offline` again, which is idempotent.
       */
      /** One hold / resume call: the task is refetched whether it was done, refused as a stale transition or refused as a typed 200 status. */
      async function runTaskControl(sessionId: string | null, taskId: string, which: "holdTask" | "resumeTask"): Promise<{ status: string }> {
        if (!sessionId) throw new Error("no lane-v2 session");
        const gen = generation;
        try {
          const out = await deps.api[which](sessionId, taskId);
          ensureScheduler(gen).schedule({ session: true, asks: false, turns: false, tasks: [taskId] });
          return { status: out.status };
        } catch (err) {
          if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
          else ensureScheduler(gen).schedule({ session: false, asks: false, turns: false, tasks: [taskId] });
          throw err;
        }
      }

      function markOffline(gen: number, retryAfterMs: number | null): void {
        if (!current(gen)) return;
        set({ offline: true });
        scheduleOfflineReprobe(gen, Math.max(1000, retryAfterMs ?? OFFLINE_REPROBE_DEFAULT_MS));
      }

      return {
        ...INITIAL_LANE_V2_STATE,

        startDraft: (sessionId, caps = {}) => {
          stopLive();
          generation += 1;
          const offline = caps.offline ?? false;
          set({
            ...INITIAL_LANE_V2_STATE,
            sessionId,
            draft: true,
            deployMode: caps.deployMode ?? null,
            turnAnswersAsk: caps.turnAnswersAsk ?? false,
            offline,
            turnAttachments: caps.turnAttachments ?? false,
            turnIdeState: caps.turnIdeState ?? false,
            branchSave: caps.branchSave ?? false,
            liveFrames: caps.liveFrames ?? false,
          });
          // Offline from the INITIAL R0 answer (not a live 503) still needs the re-probe loop — a
          // draft opened while the operator's kill switch is already on must not stay stuck banner-up
          // forever just because nothing here ever drew a 503 to trigger `markOffline`.
          if (offline) scheduleOfflineReprobe(generation, OFFLINE_REPROBE_DEFAULT_MS);
        },

        attach: async (sessionId, opts = {}) => {
          stopLive();
          generation += 1;
          const gen = generation;
          const keep = get().sessionId === sessionId ? get().localTurns : [];
          const offline = opts.offline ?? get().offline;
          set({
            ...INITIAL_LANE_V2_STATE,
            sessionId,
            draft: false,
            localTurns: keep,
            deployMode: opts.deployMode ?? get().deployMode,
            turnAnswersAsk: opts.turnAnswersAsk ?? get().turnAnswersAsk,
            offline,
            turnAttachments: opts.turnAttachments ?? get().turnAttachments,
            turnIdeState: opts.turnIdeState ?? get().turnIdeState,
            branchSave: opts.branchSave ?? get().branchSave,
            liveFrames: opts.liveFrames ?? get().liveFrames,
            streamStatus: "connecting",
            // Chunks missed while disconnected would leave a gap in the draft; R5 is the truth.
            liveDraft: null,
          });
          // Same reasoning as `startDraft`: offline from the R0 answer this attach was handed (not a
          // live 503) still needs the loop, or an existing v2 session opened while offline never gets
          // its banner cleared until some OTHER call happens to draw a fresh 503.
          if (offline) scheduleOfflineReprobe(gen, OFFLINE_REPROBE_DEFAULT_MS);
          try {
            // R1 FIRST: everything read after it reflects at least its `last_seq` (see `applyAttach`).
            const session = opts.session ?? (await deps.api.getSession(sessionId));
            const [asks, turns, snaps] = await Promise.all([
              deps.api.getAsks(sessionId, ASK_STATUS),
              deps.api.getTurns(sessionId),
              Promise.all((session.tasks ?? []).map((t) => deps.api.getTask(sessionId, t.task_id))),
            ]);
            if (!current(gen)) return;
            const snapshots: Record<string, TaskSnapshot> = {};
            for (const snap of snaps) snapshots[snap.task_id] = snap;
            set((st) => applyAttach(st, { session, asks, turns, snapshots }));
            for (const snap of snaps) resumeSaveFrom(gen, snap);
            startStream(gen, sessionId);
          } catch (err) {
            if (!current(gen)) return;
            console.warn("[lane-v2] attach failed", err);
            set({
              streamStatus: "disconnected",
              error: isLaneV2Error(err) ? describeLaneV2Error(err) : "Could not load this conversation. Try reconnecting.",
            });
          }
        },

        reconnect: async () => {
          const { sessionId, draft } = get();
          if (!sessionId || draft) return;
          set({ error: null });
          await get().attach(sessionId);
        },

        reset: () => {
          stopLive();
          generation += 1;
          set({ ...INITIAL_LANE_V2_STATE });
        },

        applyFrame: (frame) => {
          const before = get();
          const { state, refetch } = reduceFrame(before, frame);
          if (state === before) return;
          // A detached turn's "still working" notice ends with the turn.
          set(frame.type === "turn_ended" ? { ...state, notice: null } : state);
          // A turn that just opened accepted its send: let the composer let go now, not when the answer ends.
          if (frame.type === "turn_opened") {
            for (const t of state.localTurns) if (t.turnId) acceptOnOpen.get(t.localId)?.();
          }
          ensureScheduler(generation).schedule(refetch);
          if (frame.type === "turn_ended") drainQueued(generation);
        },

        refetch: async (plan) => {
          await runRefetch(generation, plan);
        },

        sendTurn: async (text, opts = {}) => {
          const trimmed = text.trim();
          const { sessionId, draft } = get();
          if (!trimmed || !sessionId) return null;
          const gen = generation;
          const localId = deps.newLocalId();
          set((st) => ({
            error: null,
            // The clock and the story start now, with this send: a new turn's activity is its own.
            workingSince: deps.now(),
            activity: [],
            liveDraft: null,
            // "Included in the reply above" is about the reply that was newest; with a new turn it is no longer above.
            followUps: st.followUps.filter((f) => f.status !== "included"),
            localTurns: [
              ...st.localTurns,
              {
                localId,
                text: trimmed,
                at: deps.now(),
                turnId: null,
                status: "sending",
                // Shown on the question at once; the bytes stay in the request only.
                attachments: (opts.attachments ?? []).map((a) => {
                  const name = a.name || "attachment";
                  const previewUrl = a.kind === "image" ? opts.previews?.[name] : undefined;
                  return previewUrl ? { name, kind: a.kind, previewUrl } : { name, kind: a.kind };
                }),
              },
            ],
          }));
          let acceptedOnce = false;
          const accept = () => {
            acceptOnOpen.delete(localId);
            if (acceptedOnce) return;
            acceptedOnce = true;
            try {
              opts.onAccepted?.();
            } catch (err) {
              console.error("sendTurn onAccepted failed", err);
            }
          };
          if (opts.onAccepted) acceptOnOpen.set(localId, accept);
          const fail = (message: string) =>
            set((st) => ({
              localTurns: st.localTurns.map((t) => (t.localId === localId ? { ...t, status: "failed", error: message } : t)),
            }));
          try {
            if (draft) await pinDraft(gen, sessionId);
          } catch (err) {
            acceptOnOpen.delete(localId);
            if (current(gen)) fail(isLaneV2Error(err) ? describeLaneV2Error(err) : "Could not start this conversation.");
            return null;
          }
          if (!current(gen)) return null;
          // Fire the (blocking) turn request; the caller is released as soon as it is in flight.
          void deps.api
            .submitTurn(sessionId, trimmed, {
              answersAskId: opts.answersAskId,
              attachments: opts.attachments,
              // Every lane-v2 turn carries the current git branch (a chat "save it" saves there), including the ones the
              // store sends itself (a queued message, an answer-then-turn). An explicit value from the caller wins.
              ideState: opts.ideState !== undefined ? opts.ideState : get().turnIdeState ? laneV2IdeState() : null,
              onBusy: ({ waitMs }) => {
                if (current(gen)) {
                  set({ notice: `The service is busy — retrying in ${Math.ceil(waitMs / 1000)} s…` });
                }
              },
            })
            .then((result) => {
              // Accepted is a fact about THIS request, whatever happened to the store since (a no-op if the turn opened first).
              accept();
              if (!current(gen)) return;
              if (result.kind === "done") {
                const out = result.out;
                set((st) => {
                  // What the reader sent while this turn ran: taken into its work, or to be sent as the next turn.
                  const settled = settleFollowUps(st.followUps, out.turn_id, followUpFactsOf(out.detail), { stopped: out.outcome === "cancelled" });
                  return {
                    notice: null,
                    followUps: settled.followUps,
                    queuedTurns: [...st.queuedTurns, ...settled.sendNext],
                    localTurns: bindLocalTurn(st.localTurns, out.turn_id, localId).map((t) =>
                      t.localId === localId ? { ...t, status: "sent", turnId: out.turn_id, markdown: out.markdown ?? null } : t,
                    ),
                  };
                });
                drainQueued(gen, out.turn_id);
                ensureScheduler(gen).schedule({
                  session: true,
                  asks: true,
                  turns: true,
                  tasks: out.task_id ? [out.task_id] : [],
                });
              } else {
                // Not a failure: the proxy let go, the turn keeps running; the stream is the truth.
                set((st) => ({
                  notice: "Still working — updates will appear here as they happen.",
                  localTurns: st.localTurns.map((t) => (t.localId === localId ? { ...t, status: "sent" } : t)),
                }));
                ensureScheduler(gen).schedule({ session: true, asks: true, turns: true, tasks: [] });
              }
            })
            .catch((err: unknown) => {
              acceptOnOpen.delete(localId);
              if (!current(gen)) return;
              set({ notice: null });
              if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
              fail(isLaneV2Error(err) ? describeLaneV2Error(err) : "Your message was not sent. You can try again.");
              // The turn failed without a result: what was sent to it mid-way is not confirmed either.
              const failedTurnId = get().localTurns.find((t) => t.localId === localId)?.turnId;
              if (failedTurnId) set((st) => ({ followUps: unconfirmFollowUps(st.followUps, failedTurnId) }));
            });
          return localId;
        },

        dismissFollowUp: (localId) => set((st) => ({ followUps: st.followUps.filter((f) => f.localId !== localId) })),

        sendFollowUp: async (text, opts) => {
          const trimmed = text.trim();
          const { sessionId, activeTurnId, followUps } = get();
          if (!trimmed || !sessionId || !activeTurnId || !canSendFollowUp(followUps, activeTurnId)) return null;
          const gen = generation;
          const turnId = activeTurnId;
          const localId = deps.newLocalId();
          set((st) => ({
            error: null,
            followUps: [...st.followUps, { localId, text: trimmed, at: deps.now(), turnId, inboxSeq: null, status: "sending", ...(opts?.withoutFile ? { withoutFile: true } : {}) }],
          }));
          try {
            const out = await deps.api.postTurnMessage(sessionId, turnId, trimmed);
            if (!current(gen)) return localId;
            // Still "sending" = the turn has not settled it yet; a settled one only learns its ordinal.
            set((st) => ({
              followUps: st.followUps.map((f) =>
                f.localId !== localId ? f : { ...f, inboxSeq: out.inbox_seq, status: f.status === "sending" ? "waiting" : f.status },
              ),
            }));
          } catch (err) {
            if (!current(gen)) return localId;
            if (isRunTerminal(err)) {
              // The turn ended before the message reached it: it becomes the next turn, never a lost message.
              set((st) => ({ followUps: st.followUps.filter((f) => f.localId !== localId), queuedTurns: [...st.queuedTurns, trimmed] }));
              drainQueued(gen, turnId);
            } else {
              // A typed refusal speaks in its own copy (offline, session or turn gone); anything else a fixed line.
              if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
              const why = steerFailureCopy(err);
              set((st) => ({
                followUps: st.followUps.map((f) => (f.localId === localId ? { ...f, status: "failed", error: why } : f)),
              }));
            }
          }
          return localId;
        },

        cancelActiveTurn: async () => {
          const { sessionId, activeTurnId, localTurns } = get();
          const turnId = activeTurnId ?? [...localTurns].reverse().find((t) => t.turnId && t.status === "sending")?.turnId ?? null;
          if (!sessionId || !turnId) return;
          try {
            await deps.api.cancelTurn(sessionId, turnId);
          } catch (err) {
            set({ error: isLaneV2Error(err) ? describeLaneV2Error(err) : "Could not stop the turn." });
          }
        },

        answerAsk: async (ask, body) => {
          const sessionId = get().sessionId;
          if (!sessionId) return;
          // Captured now, not read again after the `await` below — a slower respond racing a fresh
          // attach/reset (the user switched sessions while this was in flight) must flag OFFLINE (or
          // schedule a refetch) for the generation this call actually started in, never whatever the
          // module-level counter has moved on to by the time the response lands.
          const gen = generation;
          const route = answerRoute(ask, { turnAnswersAsk: get().turnAnswersAsk });
          if (route === "turn") {
            const text = turnTextForAnswer(ask.interaction, body);
            if (!text) return;
            await get().sendTurn(text);
            return;
          }
          let outcome: Awaited<ReturnType<typeof deps.respondVia>>;
          try {
            outcome = await deps.respondVia({ kind: "session", sessionId }, ask.interaction, body);
          } catch (err) {
            // Only a GRANTING option is offline-gated (`READ_MODEL_SPEC.md` §3.10) — a decline or any
            // other non-granting answer never reaches here as a LANE_V2_OFFLINE throw, so this only
            // ever fires for the case the spec says to flip the banner for.
            if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
            throw err;
          }
          // MSA leg `cv-connector-writes`: an approved write to a connected tool runs inside the respond, and
          // `applied_to` says what it did (written + url, or the provider's refusal). Kept by ask id so the settled
          // card says so at once; the `connector_write_done` frame repeats it for every other open view.
          const written = outcome.kind === "session" ? connectorWriteOutcomeOf(outcome.out.applied_to) : null;
          if (written && current(gen)) {
            set((st) => ({ connectorWrites: { ...st.connectorWrites, [written.ask_id]: written } }));
          }
          if (route === "respond_then_turn") {
            // The card's answer is recorded; continue the SAME ask with a deterministic turn only
            // when the server says that turn is required (never otherwise — see askRouting.ts).
            const appliedTo = outcome.kind === "session" ? (outcome.out.applied_to as { type?: string } | null | undefined) : null;
            const text = appliedTo?.type === "turn_required" ? turnTextForAnswer(ask.interaction, body) : null;
            if (text) {
              await get().sendTurn(text, { answersAskId: ask.interaction.interaction_id });
              return;
            }
          }
          ensureScheduler(gen).schedule({
            session: true,
            asks: true,
            turns: false,
            tasks: ask.task_id ? [ask.task_id] : [],
          });
        },

        cancelTask: async (taskId) => {
          const sessionId = get().sessionId;
          if (!sessionId) return { cancelled: false, reason: null };
          const out = await deps.api.cancelTask(sessionId, taskId);
          ensureScheduler(generation).schedule({ session: true, asks: true, turns: false, tasks: [taskId] });
          return { cancelled: out.cancelled === true, reason: out.reason ?? null };
        },

        holdTask: async (taskId) => runTaskControl(get().sessionId, taskId, "holdTask"),
        resumeTask: async (taskId) => runTaskControl(get().sessionId, taskId, "resumeTask"),

        revertTask: async (taskId) => {
          const sessionId = get().sessionId;
          if (!sessionId) return;
          // Captured now — see `answerAsk`'s identical comment.
          const gen = generation;
          let out: Awaited<ReturnType<typeof deps.api.revertTask>>;
          try {
            out = await deps.api.revertTask(sessionId, taskId);
          } catch (err) {
            if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
            throw err;
          }
          ensureScheduler(gen).schedule({
            session: true,
            asks: true,
            turns: false,
            tasks: [taskId, ...(out?.task_id ? [out.task_id] : [])],
          });
        },

        saveBranch: async (taskId, body, opts) => {
          const sessionId = get().sessionId;
          if (!sessionId) throw new Error("no lane-v2 session");
          // Captured now — see `answerAsk`'s identical comment.
          const gen = generation;
          const before = get().snapshots[taskId]?.package?.branch_saves?.[0] ?? null;
          const signal = opts?.signal;
          const ctl = new AbortController();
          const relay = () => ctl.abort();
          signal?.addEventListener("abort", relay, { once: true });
          setSave(taskId, null);
          /** Fetch R3 now (not debounced): the verdicts below read it. */
          const readR3 = async (): Promise<TaskSnapshot | null> => {
            try {
              const snap = await deps.api.getTask(sessionId, taskId);
              if (current(gen)) applySnap(gen, snap);
              return snap;
            } catch (err) {
              console.warn("[lane-v2] task refetch after save failed", err);
              return null;
            }
          };
          let accepted = false;
          try {
            const out = await runBranchSave({
              post: () => deps.api.saveBranch(sessionId, taskId, body, { signal: ctl.signal }),
              ...pollDeps(sessionId, taskId, ctl.signal),
              onAccepted: (saveId) => {
                accepted = true;
                activeSaves.get(taskId)?.ctl.abort();
                activeSaves.set(taskId, { saveId, ctl });
              },
              onProgress: (chunks) => {
                opts?.onProgress?.(chunks);
                const tracked = [...activeSaves.entries()].find(([id, a]) => id === taskId && a.ctl === ctl);
                if (tracked && current(gen)) setSave(taskId, { phase: "running", saveId: tracked[1].saveId, mode: "async", chunks });
              },
            });
            return { kind: "saved", out } satisfies SaveBranchSettled;
          } catch (err) {
            if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
            if (ctl.signal.aborted || isAbortError(err)) throw err;
            if (isLaneV2Error(err) && err.code === "BRANCH_SAVE_IN_PROGRESS") {
              // Somebody else's save holds the slot: R3 says whose (and starts following it); the panel renders that.
              await readR3();
              throw err;
            }
            // Only when the POST itself failed: once the 202 arrived the server answered, and an interrupted/gone poll is not a lost answer.
            if (!accepted && isLostAnswer(err)) {
              // Lost answer: the save may have run on. R3 decides (running → following it; newer history row → wrote_some, never success).
              const snap = await readR3();
              if (snap) {
                const verdict = deriveAfterCut(before, snap.package, body.branch);
                if (verdict.kind === "running") return { kind: "still_running", running: verdict.running } satisfies SaveBranchSettled;
                if (verdict.kind === "wrote_some") return { kind: "wrote_some", record: verdict.record } satisfies SaveBranchSettled;
              }
            }
            throw err;
          } finally {
            signal?.removeEventListener("abort", relay);
            if (activeSaves.get(taskId)?.ctl === ctl) activeSaves.delete(taskId);
            // A save (even a failed, interrupted or abandoned one, which may have written some rows) adds/changes R3
            // package.branch_saves — worth a refetch, but it changes neither the session/asks list nor the transcript.
            ensureScheduler(gen).schedule({ session: false, asks: false, turns: false, tasks: [taskId] });
          }
        },

        validateTask: async (taskId, idempotencyKey) => {
          const sessionId = get().sessionId;
          if (!sessionId) throw new Error("no lane-v2 session");
          // Captured now — see `answerAsk`'s identical comment.
          const gen = generation;
          let out: Awaited<ReturnType<typeof deps.api.validateTask>>;
          try {
            out = await deps.api.validateTask(sessionId, taskId, idempotencyKey);
          } catch (err) {
            if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
            throw err;
          }
          // The task may have moved (validated → checking) and gained a check row; the session list and asks are unchanged.
          ensureScheduler(gen).schedule({ session: false, asks: false, turns: false, tasks: [taskId] });
          return out;
        },

        requestChanges: async (taskId, text, idempotencyKey) => {
          const sessionId = get().sessionId;
          if (!sessionId) throw new Error("no lane-v2 session");
          const gen = generation;
          let out: Awaited<ReturnType<typeof deps.api.requestChanges>>;
          try {
            out = await deps.api.requestChanges(sessionId, taskId, text, idempotencyKey);
          } catch (err) {
            if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
            // A refusal means the task moved on since it was shown: refetch it so its `actions` stop offering the button.
            if (isLaneV2Error(err) && REST_ACTION_REFUSALS.has(err.code ?? "")) ensureScheduler(gen).schedule({ session: false, asks: false, turns: false, tasks: [taskId] });
            throw err;
          }
          // The task re-enters planning (new plan round, asks, transcript rows): refetch the lot, not just the task.
          ensureScheduler(gen).schedule({ session: true, asks: true, turns: true, tasks: [taskId] });
          return out;
        },

        discardTask: async (taskId, idempotencyKey) => {
          const sessionId = get().sessionId;
          if (!sessionId) throw new Error("no lane-v2 session");
          const gen = generation;
          let out: Awaited<ReturnType<typeof deps.api.discardTask>>;
          try {
            out = await deps.api.discardTask(sessionId, taskId, idempotencyKey);
          } catch (err) {
            if (isLaneV2Error(err) && err.code === "LANE_V2_OFFLINE") markOffline(gen, err.retryAfterMs);
            // A refusal means the task moved on since it was shown: refetch it so its `actions` stop offering the button.
            if (isLaneV2Error(err) && REST_ACTION_REFUSALS.has(err.code ?? "")) ensureScheduler(gen).schedule({ session: false, asks: false, turns: false, tasks: [taskId] });
            throw err;
          }
          ensureScheduler(gen).schedule({ session: true, asks: true, turns: true, tasks: [taskId] });
          return out;
        },

        loadItemBody: async (taskId, workItemId) => {
          const sessionId = get().sessionId;
          if (!sessionId) throw new Error("no lane-v2 session");
          return deps.api.getItemBody(sessionId, taskId, workItemId);
        },
      };
    },
    {
      name: PERSIST_NAME,
      storage: createOrgScopedJSONStorage(PERSIST_NAME),
      version: 1,
      // Only a PINNED session is worth restoring; a draft is a UUID nothing was sent on.
      partialize: (state) => ({ sessionId: state.draft ? null : state.sessionId }),
      merge: (persisted, currentState) => {
        const stored = (persisted ?? {}) as { sessionId?: unknown };
        return {
          ...currentState,
          sessionId: typeof stored.sessionId === "string" && stored.sessionId ? stored.sessionId : null,
        };
      },
    },
  ),
);

/** The active v2 session id this browser last pinned (restored on reload), or null. */
export function persistedLaneV2SessionId(): string | null {
  const s = useLaneV2SessionStore.getState();
  return s.draft ? null : s.sessionId;
}

registerOrgScopedReset(PERSIST_NAME, () => useLaneV2SessionStore.getState().reset());
